import { useEffect, useState } from "react";
import { CalendarClock, Repeat } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SlotsManager } from "@/pages/admin/PropertyCampaigns";
import { TechnicianWeekGrid } from "@/components/admin/TechnicianWeekGrid";
import LocationDayMatrix from "@/components/admin/LocationDayMatrix";
import { RosterGuardrailDialog } from "@/components/admin/RosterGuardrailDialog";
import { CapacityGuardrailDialog } from "@/components/admin/CapacityGuardrailDialog";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function RecurringRules() {
  const [rules, setRules] = useState([]);
  const [techs, setTechs] = useState([]);
  const [form, setForm] = useState({ weekday: "1", start_time: "08:00", end_time: "16:00", capacity: 4, technician_id: "" });
  const [capEdits, setCapEdits] = useState({});
  const [refresh, setRefresh] = useState(0);
  const [guardrail, setGuardrail] = useState(null); // {rid, warning}
  const [capGuardrail, setCapGuardrail] = useState(null); // {rid, capacity, warning}

  useEffect(() => {
    api.get("/admin/availability/recurring").then(({ data }) => setRules(data.rules || [])).catch(() => {});
    api.get("/field/technicians").then(({ data }) => setTechs(data.technicians || [])).catch(() => {});
  }, [refresh]);

  const call = (fn, ok) => fn.then(({ data }) => { toast.success(typeof ok === "function" ? ok(data) : ok); setRefresh((x) => x + 1); })
    .catch((e) => toast.error(e.response?.data?.detail || "Failed"));

  const pause = (r, confirm) => {
    api.patch(`/admin/availability/recurring/${r.id}`, { active: false, confirm })
      .then(() => { toast.success("Rule paused — future unbooked slots removed, partially-booked slots closed to new bookings"); setGuardrail(null); setRefresh((x) => x + 1); })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail?.requires_confirmation) {
          setGuardrail({ rid: r.id, warning: detail });
        } else {
          toast.error(detail || "Failed");
        }
      });
  };

  const add = (e) => {
    e.preventDefault();
    call(api.post("/admin/availability/recurring", { ...form, weekday: Number(form.weekday), technician_id: form.technician_id || null }),
      (d) => `Rule saved — ${d.slots_generated} slots generated ahead`);
  };
  const saveCapacity = (r, confirm) => {
    const next = confirm ? capGuardrail.capacity : Number(capEdits[r.id]);
    if (!next || (!confirm && next === r.capacity)) return;
    api.patch(`/admin/availability/recurring/${r.id}`, { capacity: next, confirm })
      .then(() => {
        toast.success(`Capacity updated to ${next} — already-generated future slots updated too`);
        setCapGuardrail(null);
        setCapEdits((c) => { const { [r.id]: _, ...rest } = c; return rest; });
        setRefresh((x) => x + 1);
      })
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (e.response?.status === 409 && detail?.requires_confirmation) {
          setCapGuardrail({ rid: r.id, capacity: next, warning: detail });
        } else {
          toast.error(detail || "Failed");
        }
      });
  };
  const techName = (id) => techs.find((t) => t.id === id)?.name || "Unassigned";

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-6" data-testid="recurring-rules-card">
      <RosterGuardrailDialog
        warning={guardrail?.warning}
        onCancel={() => setGuardrail(null)}
        onConfirm={() => guardrail && pause({ id: guardrail.rid }, true)} />
      <CapacityGuardrailDialog
        warning={capGuardrail?.warning}
        onCancel={() => setCapGuardrail(null)}
        onConfirm={() => capGuardrail && saveCapacity({ id: capGuardrail.rid, capacity: 0 }, true)} />
      <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-1 flex items-center gap-1.5">
        <Repeat className="w-4 h-4" /> Weekly recurring times
      </h2>
      <p className="text-xs text-slate-500 mb-3">Set it once — slots generate themselves 4 weeks ahead automatically and keep rolling forward. Turning a rule off removes its future unbooked slots.</p>
      <div className="space-y-2 mb-4">
        {rules.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm" data-testid={`recurring-rule-${r.id}`}>
            <span className="font-semibold text-slate-800">Every {DAYS[r.weekday]}</span>
            <span className="text-slate-600">{r.start_time}–{r.end_time}</span>
            <span className="text-slate-500 text-xs">{techName(r.technician_id)}</span>
            <span className="flex items-center gap-1 text-xs text-slate-500">
              cap
              <input type="number" min="1" max="50"
                value={capEdits[r.id] ?? r.capacity}
                onChange={(e) => setCapEdits((c) => ({ ...c, [r.id]: e.target.value }))}
                data-testid={`recurring-rule-capacity-${r.id}`}
                className="w-14 rounded-lg border border-slate-300 px-1.5 py-1 text-xs" />
            </span>
            {Number(capEdits[r.id]) > 0 && Number(capEdits[r.id]) !== r.capacity && (
              <button data-testid={`recurring-rule-capacity-save-${r.id}`} onClick={() => saveCapacity(r, false)}
                className="text-xs font-semibold text-cyan-700 hover:underline">Save</button>
            )}
            <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{r.active ? "Active" : "Paused"}</span>
            <span className="flex-1" />
            <button data-testid={`recurring-toggle-${r.id}`} onClick={() => r.active ? pause(r, false) : call(api.patch(`/admin/availability/recurring/${r.id}`, { active: true }), "Rule resumed — slots regenerated")}
              className="text-xs font-semibold text-cyan-700 hover:underline">{r.active ? "Pause" : "Resume"}</button>
            <button data-testid={`recurring-delete-${r.id}`} onClick={() => window.confirm("Delete this rule and its future unbooked slots?") && call(api.delete(`/admin/availability/recurring/${r.id}`), "Rule deleted")}
              className="text-xs font-semibold text-rose-600 hover:underline">Delete</button>
          </div>
        ))}
        {rules.length === 0 && <div className="text-xs text-slate-400 italic">No recurring rules yet</div>}
      </div>
      <form onSubmit={add} className="flex flex-wrap items-end gap-2">
        <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })} data-testid="recurring-weekday" className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
          {DAYS.map((d, i) => <option key={d} value={i}>Every {d}</option>)}
        </select>
        <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="recurring-start" className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="recurring-end" className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        <input type="number" min="1" max="50" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="recurring-capacity" title="Capacity" className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        <select value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })} data-testid="recurring-tech" className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
          <option value="">Unassigned</option>
          {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button type="submit" data-testid="recurring-add-btn" className="rounded-full bg-cyan-700 text-white text-sm font-bold px-4 py-1.5 hover:bg-cyan-800">Add rule</button>
      </form>
    </div>
  );
}

export default function AdminAvailability() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-availability-page">
      <div className="flex items-center gap-2">
        <CalendarClock className="w-5 h-5 text-cyan-700" />
        <h1 className="text-2xl font-bold text-slate-900">Availability</h1>
      </div>
      <p className="text-sm text-slate-500 mt-1 mb-6 max-w-2xl">
        One global pool of appointment days and times for every technician. Every property QR code —
        from any campaign, no matter how old — books from this pool, so changes here apply everywhere
        instantly. ServiceM8 cross-checks, buffers and minimum-notice rules still apply.
      </p>
      <TechnicianWeekGrid />
      <LocationDayMatrix />
      <details className="mb-6" data-testid="availability-advanced">
        <summary className="cursor-pointer text-xs font-bold text-slate-500 hover:text-slate-700 uppercase tracking-wider">
          Advanced — custom rules, one-off dates & booked slots
        </summary>
        <div className="mt-4">
          <RecurringRules />
          <SlotsManager campaignId="global" basePath="/admin/availability/slots" onChange={() => {}} refreshKey={0} />
        </div>
      </details>
    </div>
  );
}
